import { useEffect, useState } from "react";
import { Bookmark, ChevronLeft, ChevronRight, LayoutGrid, List, Maximize, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Floating top-centre viewer controls: zoom − / % / +, Fit, Sheets, page
 * nav with editable page number, bookmark toggle, scale badge.
 */
export default function ViewerToolbar({
  zoom, onZoom, onFit, onOpenGallery, isPdf, isTiff, numPages, pageNum, onPage,
  bookmarked, onToggleBookmark, scaleLabel, snap, snapVisible, onToggleSnap,
  sheetNav,
}) {
  const [pageInput, setPageInput] = useState(String(pageNum));
  useEffect(() => { setPageInput(String(sheetNav ? sheetNav.index + 1 : pageNum)); }, [pageNum, sheetNav?.index]); // eslint-disable-line
  const commitPage = () => {
    const n = parseInt(pageInput, 10);
    if (!Number.isFinite(n)) { setPageInput(String(sheetNav ? sheetNav.index + 1 : pageNum)); return; }
    if (sheetNav) sheetNav.onJump(Math.max(0, Math.min(sheetNav.total - 1, n - 1)));
    else onPage(Math.max(1, Math.min(numPages, n)));
  };
  const b = "flex h-8 w-8 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30";
  return (
    <div className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2">
      <div className="pointer-events-auto flex items-center gap-0.5 rounded-full border border-[#24324a] bg-[#0b172a]/95 px-2 py-1 shadow-xl backdrop-blur" data-testid="takeoff-viewer-toolbar">
        <button className={b} onClick={() => onZoom(zoom - 10)} aria-label="Zoom out" data-testid="viewer-zoom-out"><ZoomOut className="h-4 w-4" /></button>
        <span className="w-12 text-center font-mono text-xs text-white/90" data-testid="viewer-zoom-pct">{Math.round(zoom)}%</span>
        <button className={b} onClick={() => onZoom(zoom + 10)} aria-label="Zoom in" data-testid="viewer-zoom-in"><ZoomIn className="h-4 w-4" /></button>
        <button className={b} onClick={onFit} aria-label="Fit page" data-testid="viewer-fit"><Maximize className="h-4 w-4" /></button>
        {snapVisible && (
          <button
            onClick={onToggleSnap}
            className={`ml-1 h-7 rounded-full px-2.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
              snap ? "bg-[#f97316] text-[#0b172a]" : "bg-white/10 text-white/70 hover:bg-white/15"
            }`}
            title="Angle snap for the linear tool (S)"
            aria-pressed={!!snap}
            data-testid="viewer-snap-toggle"
          >
            {snap ? "Snap ON · 45°" : "Snap OFF · Any angle"}
          </button>
        )}
        {(isPdf || isTiff || sheetNav) && (
          <>
            <div className="mx-1 h-5 w-px bg-white/15" />
            {(isPdf || isTiff) && (
              <button className={b} onClick={onOpenGallery} aria-label="Sheets gallery" data-testid="viewer-sheets"><LayoutGrid className="h-4 w-4" /></button>
            )}
            {sheetNav ? (
              <>
                {/* folder-wide browsing: arrows cross file boundaries,
                    disabled only at the folder's first/last sheet */}
                <button className={b} onClick={sheetNav.onPrev} disabled={sheetNav.index <= 0} aria-label="Previous sheet in folder" data-testid="viewer-prev-page"><ChevronLeft className="h-4 w-4" /></button>
                <span className="flex items-center gap-1 font-mono text-xs text-white/90">
                  <input
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ""))}
                    onBlur={commitPage}
                    onKeyDown={(e) => { if (e.key === "Enter") { commitPage(); e.currentTarget.blur(); } }}
                    aria-label="Sheet number in folder"
                    data-testid="viewer-page-input"
                    className="tk-dark-input h-6 w-9 rounded border border-white/20 bg-white/10 text-center text-xs text-white focus:border-[#f97316] focus:outline-none"
                  />
                  <span className="text-white/50" data-testid="viewer-sheet-total">/ {sheetNav.total}</span>
                </span>
                <button className={b} onClick={sheetNav.onNext} disabled={sheetNav.index >= sheetNav.total - 1} aria-label="Next sheet in folder" data-testid="viewer-next-page"><ChevronRight className="h-4 w-4" /></button>
                <span className="max-w-[150px] truncate text-[10px] font-medium text-white/60" title={sheetNav.name} data-testid="viewer-sheet-name">{sheetNav.name}</span>
                <button className={b} onClick={sheetNav.onOpenList} aria-label="All sheets in this folder" title="All sheets in this folder" data-testid="viewer-folder-sheets"><List className="h-4 w-4" /></button>
              </>
            ) : (
              <>
                <button className={b} onClick={() => onPage(pageNum - 1)} disabled={pageNum <= 1} aria-label="Previous page" data-testid="viewer-prev-page-legacy"><ChevronLeft className="h-4 w-4" /></button>
                <span className="flex items-center gap-1 font-mono text-xs text-white/90">
                  <input
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ""))}
                    onBlur={commitPage}
                    onKeyDown={(e) => { if (e.key === "Enter") { commitPage(); e.currentTarget.blur(); } }}
                    aria-label="Page number"
                    data-testid="viewer-page-input-legacy"
                    className="tk-dark-input h-6 w-9 rounded border border-white/20 bg-white/10 text-center text-xs text-white focus:border-[#f97316] focus:outline-none"
                  />
                  <span className="text-white/50">/ {numPages}</span>
                </span>
                <button className={b} onClick={() => onPage(pageNum + 1)} disabled={pageNum >= numPages} aria-label="Next page" data-testid="viewer-next-page-legacy"><ChevronRight className="h-4 w-4" /></button>
              </>
            )}
          </>
        )}
        <div className="mx-1 h-5 w-px bg-white/15" />
        <button className={b} onClick={onToggleBookmark} aria-label={bookmarked ? "Remove bookmark" : "Bookmark this sheet"} aria-pressed={bookmarked} data-testid="viewer-bookmark">
          <Bookmark className={`h-4 w-4 ${bookmarked ? "fill-[#f97316] text-[#f97316]" : ""}`} />
        </button>
        <span
          className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${scaleLabel && scaleLabel !== "No scale" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}
          data-testid="viewer-scale-badge"
        >
          {scaleLabel || "No scale"}
        </span>
      </div>
    </div>
  );
}
